import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Gauge, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "staff" ? "/app" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(u.role === "staff" ? "/app" : "/admin", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex relative grain bg-slate-900 text-white p-14 flex-col justify-between overflow-hidden">
        <BrandMark light />
        <div className="relative max-w-lg fade-up">
          <div className="text-xs uppercase tracking-[0.25em] text-sky-400 mb-5">Workforce Operations System</div>
          <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight">
            Every engineer, every pipeline, every rupee — in one console.
          </h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Geo-verified attendance, MGPS &amp; equipment service tickets with signed proof, sales pipeline and automated payroll for Upkar Healthcare.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-4 text-sm">
          {[[MapPin, "GPS + selfie clock-in"], [Gauge, "PSI-verified MGPS work"], [ShieldCheck, "Director-approved payroll"]].map(([I, t]) => (
            <div key={t} className="border border-slate-700 rounded-xl p-4">
              <I className="h-5 w-5 text-sky-400 mb-3" />
              <div className="text-slate-300">{t}</div>
            </div>
          ))}
        </div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full border-[40px] border-sky-600/20" />
      </section>
      <section className="flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-10"><BrandMark /></div>
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Sign in</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Use the account created for you by HR or the Director.</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" data-testid="login-password-input" />
            </div>
            {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-base" data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-8">No self sign-up. Contact HR if you cannot access your account.</p>
        </form>
      </section>
    </div>
  );
}
